import { useContext, useEffect, useRef, useState } from "react";
import AuthContext from "../../../store/auth-context";
import { GlobalContext } from "../../../store/global-context";
import axios from "axios";
import styles from "./Portfolio.module.css";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { PiBagFill } from "react-icons/pi";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { IoAdd } from "react-icons/io5";
import { FloatLabel } from 'primereact/floatlabel';
import { InputText } from "primereact/inputtext";
import { useNavigate } from "react-router-dom";
import { IoMdDownload } from "react-icons/io";
import { errorMessage } from "../../Util/errorMessage";
import { useDialogIsolation } from "../../Util/useDialogIsolation";
import Select from "../../Util/Select";
import PageHeading from "../../Util/PageHeading";
import { columnProps, tablePassThrough, useTableScrollRegion } from "../../Util/dataTable";
import { busyButtonProps } from "../../Util/busyButton";
import { useDialogSession } from "../../Util/formSession";

const Portfolio = () => {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const globalCtx = useContext(GlobalContext);
  
  // Ticket 22 (Q35): true while an Add is on its way — the Add button stays focused and says it is busy (v1.2 had no
  // busy state, so a second press sent a second request)
  const [isAddingNew, setAddingNew] = useState(false);
  // Ticket 22: which opening of the dialog a reply belongs to (Util/formSession.js)
  const dialogSession = useDialogSession();
  const [listOfCompanies, setListOfCompanies] = useState([]);
  const [listPortfolio, setListPortfolio] = useState([]);
  const [isAddNewModalVisible, setIsAddNewModalVisible] = useState(false);
  // RCMC-23: page behind inert while open, focus to the first field; RCMC-02: named close button
  const addDialogIsolation = useDialogIsolation(isAddNewModalVisible, "Add New Holding");
  const [selectedRowCount, setSelectedRowCount] = useState(10);
  const [selectedNewValues, setSelectedNewValues] = useState({
    company_id:'',
    folio_no:'',
    certificate_no:''
  });
  const dt = useRef(null);
  // RCMC-04 (ticket 16): the table's region is a Tab stop while the table is too wide for it
  useTableScrollRegion(dt);
  useEffect(() => {
    axios
      .get("/api/user/portfolio-details", {
        headers: { Authorization: "Bearer " + authCtx.authTokens.access },
      })
      .then((res) => {
        setListPortfolio(res.data.holding_details);
      })
      .catch((e) => {
        globalCtx.raiseAlert(errorMessage(e), "error");
      });

      axios.get(`/api/operations/get-companies`, {
        headers: { Authorization: "Bearer " + authCtx.authTokens.access },
      })
        .then(response => {
          setListOfCompanies(response.data.map((item) => ({label:item.company_name,value:item.id})));
        })
        .catch((e) => {
            globalCtx.raiseAlert(errorMessage(e), "error");
            setListOfCompanies([]);
            
        });
  }, []);

  const exportCSV = (selectionOnly) => {
    dt.current.exportCSV({ selectionOnly });
  };

  const rowCounts = [
    { label: "10", value: 10 },
    { label: "20", value: 20 },
    { label: "100", value: 100 },
  ];

  const openModalHandler = () => {
    dialogSession.renew();
    setIsAddNewModalVisible(true);
    setSelectedNewValues({company_id:'',folio_no:'',certificate_no:''});
  }

  const closeModalHandler = () => {
    dialogSession.renew(); // a reply still on its way belongs to a closed dialog now
    setAddingNew(false);
    setIsAddNewModalVisible(false);
    setSelectedNewValues({
      company_id:'',
      folio_no:'',
      certificate_no:''
    });
  }

  const addNewPortfolioMapping = (event) => {
    setAddingNew(true);
    const isCurrent = dialogSession.capture();
    axios
    .post("/api/user/portfolio-details", selectedNewValues , {
      headers: { Authorization: "Bearer " + authCtx.authTokens.access },
    })
    .then((res) => {
      setListPortfolio(prevState => [...prevState,...res.data.holding_details]);
      // The dialog that sent it closes; one closed (or opened again) since is left as it is
      if (isCurrent()) closeModalHandler();
      // RCMC-17: announce the outcome. Name the company from the reply; if the folio has no holding
      // rows yet, fall back to the company the Investor picked.
      const company = res.data.holding_details[0]?.company_name
        ?? listOfCompanies.find((item) => item.value === selectedNewValues.company_id)?.label;
      globalCtx.raiseAlert(company ? `Holding added to your portfolio — ${company}.` : "Holding added to your portfolio.", "success");
    })
    .catch((e) => {
      globalCtx.raiseAlert(errorMessage(e), "error");
      if (isCurrent()) setAddingNew(false); // focus is still on Add, ready for another try
    });
  }
  // RCMC-04 (ticket 16): `table-page` / `table-card` let the page flow below 768px (App.css)
  return (
    <div className="table-page" style={{ padding: "10px", maxHeight: "100vh", overflow: "hidden" }}>
      {/* RCMC-04 (ticket 16): the page name and Add New wrap onto two lines when the window is too narrow for one */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "space-between", alignItems:"center",paddingBottom:"10px" }}>
        {/* RCMC-08 (ticket 11): the page name is the page's one h1, same look */}
        <PageHeading icon={<PiBagFill style={{marginRight:"5px"}} />}>Portfolio</PageHeading>
        <button type="button"
          onClick={openModalHandler}
          style={{ background: "var(--portal-primary)", border:"none",boxShadow:"rgba(0, 0, 0, 0.24) 0px 3px 8px",borderRadius:"5px",color:"var(--portal-on-primary)", padding: "8px 20px",display:'flex',alignItems:"center"}}
        ><IoAdd size={15} />Add New</button>
        <Dialog
          header="Add New Holding"
          visible={isAddNewModalVisible}
          style={{ width: "25vw" }}
          onHide={closeModalHandler}
          {...addDialogIsolation}
        >
          {/* RCMC-02: a native select, announced as "Company" with the chosen company's name; the
              company id stays the option value and reaches the payload unchanged (a number) */}
          <Select
            id="add-holding-company"
            label="Company"
            placeholder="Select a company"
            options={listOfCompanies}
            value={selectedNewValues.company_id}
            onChange={(companyId) => setSelectedNewValues((prevState) => ({...prevState,company_id:companyId}))}
            className={styles["dialog-field"]}
          />
          {/* htmlFor, not v1.2's `for`: the same label link in the page, without React's invalid-property warning (ticket 18) */}
          <FloatLabel style={{marginBottom:"15px",width:"100%"}}>
              <InputText id="folio-no" value={selectedNewValues.folio_no} onChange={(e) => setSelectedNewValues((prevState) => ({...prevState,folio_no:e.target.value}))} />
              <label htmlFor="folio-no">Folio No / DP-Client ID</label>
          </FloatLabel>
          <FloatLabel style={{marginBottom:"15px",width:"100%"}}>
              <InputText id="certificate-no" value={selectedNewValues.certificate_no} onChange={(e) => setSelectedNewValues((prevState) => ({...prevState,certificate_no:e.target.value}))} />
              <label htmlFor="certificate-no">Certificate No / PAN No</label>
          </FloatLabel>
          {/* Ticket 22 (Q35): busy without being disabled, so focus stays on it (Util/busyButton.js) */}
          <Button label="Add" raised style={{width:"100%"}} {...busyButtonProps(isAddingNew, addNewPortfolioMapping)} />
        </Dialog>
      </div>
      <div
        className="table-card"
        style={{
          background: "white",
          borderRadius: "10px",
          maxHeight: "90%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          fontSize: "14px",
          boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
        }}
      >
        <div
          style={{
            display: "flex",
            paddingBlock: "5px",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex",paddingLeft:"10px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              {/* RCMC-10 (ticket 13): the shared native select, labelled (v1.2's PrimeReact Dropdown was unnamed) */}
              <Select
                id="portfolio-rows-per-page"
                label="Rows per page"
                inline
                options={rowCounts}
                value={selectedRowCount}
                onChange={setSelectedRowCount}
              />
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginRight: "10px",
            }}
          >
            {/* RCMC-02 (ticket 13): named for what it exports; v1.2's data-pr-tooltip did nothing (no <Tooltip>) */}
            <Button
              type="button"
              rounded
              onClick={() => exportCSV(false)}
              aria-label="Export portfolio as CSV"
            ><IoMdDownload aria-hidden="true" /></Button>
          </div>
        </div>
        <DataTable
          ref={dt}
          value={listPortfolio}
          paginator
          rows={selectedRowCount}
          stripedRows
          size="small"
          tableStyle={{ fontSize: "14px" }} // RCMC-04 (ticket 16): no fixed 50rem minimum; too wide, it scrolls in its region
          pt={tablePassThrough()} // RCMC-10 (ticket 13): named by the h1, valid row groups, scoped headers, no clickable rows; RCMC-04 (16): its region
        >
          <Column field="company_name" {...columnProps("Company Name", { sortable: true })}></Column>
          <Column field="isin" {...columnProps("ISIN No", { sortable: true })}></Column>
          <Column field="folio_no" {...columnProps("Folio No / DP-Client ID", { sortable: true })}></Column>
          <Column field="total_shares" {...columnProps("Total Shares", { sortable: true })}></Column>
          <Column field="last_updated_on" {...columnProps("Last Updated On", { sortable: true })}></Column>
        </DataTable>
      </div>
    </div>
  );
};

export default Portfolio;
